import { Link } from 'react-router-dom';
import { Button } from '@components/button';
import { Box, Link as MuiLink, Typography, Grid, ThemeProvider } from '@mui/material';
import logo from '@assets/svgs/logo.svg';
import SpecialtyLogo from '@assets/svgs/lsic-logo.svg';
import { useComposeTheme } from '@theme/theme';
import { HeaderLogo } from '@components/header/HeaderLogo';

interface IForbidden {
  title: string;
  subtitle: string[];
  items?: string[];
  isMaintenance?: boolean;
  primaryBtnText?: string;
  primaryBtnAction?: () => void;
  primaryBtnRedirect?: string;
  secondaryBtnText?: string;
  secondaryBtnAction?: () => void;
  secondaryBtnRedirect?: string;
  /** if true will make the component full screen using a custom header with nothing on it expect LIO logo */
  fullScreen?: boolean;
}

type ItemProps = {
  text: string;
};

type ButtonProps = {
  text: string;
  variant: 'contained' | 'outlined' | 'text';
};

type RedirectButtonProps = ButtonProps & {
  redirectUrl: string;
};

type ActionButtonProps = ButtonProps & {
  action: () => void;
};

const Item = ({ text }: ItemProps) => (
  <li style={{ color: 'common.white' }}>
    <Typography variant='body'>{text}</Typography>
  </li>
);

const ActionButton = ({ text, action, variant }: ActionButtonProps) => (
  <Button variant={variant} onClick={action} sx={{ margin: '16px 8px 0 8px' }}>
    {text}
  </Button>
);

const RedirectButton = ({ text, redirectUrl, variant }: RedirectButtonProps) => (
  <MuiLink to={redirectUrl} component={Link} sx={{ margin: '16px 8px 0 8px', textDecoration: 'none' }}>
    <Button variant={variant}>{text}</Button>
  </MuiLink>
);

export function Forbidden({
  title,
  subtitle,
  items,
  isMaintenance,
  primaryBtnText,
  primaryBtnRedirect,
  primaryBtnAction,
  secondaryBtnText,
  secondaryBtnAction,
  secondaryBtnRedirect,
  fullScreen,
}: IForbidden) {
  const theme = useComposeTheme('dark');

  const storedLogo = localStorage.getItem('logo') ?? '';
  const showSpecialtyLogo = storedLogo === 'lsic-logo';

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        sx={[
          {
            justifyContent: 'center',
            alignItems: 'center',
            background: ({ palette }) => (isMaintenance ? palette.background.gradient : palette.background.main),
            minHeight: '100vh',
            height: '100%',
          },
          fullScreen
            ? {
                position: 'absolute',
                width: '100vw',
                zIndex: 1000,
                top: 0,
              }
            : {},
        ]}
      >
        {fullScreen && (
          <Grid
            container
            sx={{
              background: ({ palette }) => palette.background.gradient,
              height: 72,
              position: 'absolute',
              top: 0,
              width: '100vw',
            }}
          >
            <HeaderLogo
              containerStyle={{ pl: '24px', pt: '12px' }}
              iconStyle={{
                height: 80,
                width: 80,
              }}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 11, md: 10, lg: 8, xl: 6 }}>
          <Grid
            container
            sx={{
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid>
              <img
                src={showSpecialtyLogo ? SpecialtyLogo : logo}
                alt={showSpecialtyLogo ? 'lsci-logo' : 'logo'}
                width='136px'
                height='136px'
              />
            </Grid>
            <Grid
              size={{ xs: 11 }}
              sx={{
                paddingTop: '32px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='h3'>{title}</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'center',
                    maxWidth: isMaintenance ? '400px' : '530px',
                    paddingTop: '23px',
                  }}
                >
                  {isMaintenance
                    ? subtitle?.map((el: string, index: number) =>
                        index === 0 ? (
                          <Typography key={el} variant='body'>
                            {el}
                          </Typography>
                        ) : (
                          <Typography
                            key={el}
                            variant='body'
                            sx={{
                              marginTop: '20px',
                              marginBottom: '20px',
                              fontWeight: 'bold',
                            }}
                          >
                            {el}
                          </Typography>
                        ),
                      )
                    : subtitle?.map((el: string) => (
                        <Typography key={el} variant='body'>
                          {el}
                        </Typography>
                      ))}
                  {items?.length ? (
                    <ul style={{ padding: '0px 0px 0px 25px', color: 'white' }}>
                      {items.map((el: string) => (
                        <Item key={el} text={el} />
                      ))}
                    </ul>
                  ) : null}
                </Box>
              </Box>
            </Grid>
            <Box
              sx={{
                display: 'flex',
              }}
            >
              {secondaryBtnText && secondaryBtnRedirect ? (
                <RedirectButton redirectUrl={secondaryBtnRedirect} text={secondaryBtnText} variant='outlined' />
              ) : null}
              {secondaryBtnText && secondaryBtnAction ? (
                <ActionButton action={secondaryBtnAction} text={secondaryBtnText} variant='outlined' />
              ) : null}
              {primaryBtnText && primaryBtnRedirect ? (
                <RedirectButton redirectUrl={primaryBtnRedirect} text={primaryBtnText} variant='contained' />
              ) : null}
              {primaryBtnText && primaryBtnAction ? (
                <ActionButton action={primaryBtnAction} text={primaryBtnText} variant='contained' />
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}
